import React from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Container, Paper, Box, Typography, Button, IconButton, Divider, CircularProgress, Backdrop } from '@mui/material';
import ShareIcon from '@mui/icons-material/Share';
import LocalHospitalIcon from '@mui/icons-material/LocalHospital';
import { ThemeProvider } from '@mui/material/styles';
import theme from '../../../theme/theme.js';
import axios from 'axios';
import { API_BASE_URL } from '../../../lib/constants';

import { useOperationSheet } from './hooks/useOperationSheet';
import DiagnosisSection from './screens/DiagnosisSection';
import ProcedureDetailsSection from './screens/ProcedureDetailsSection';
import TeamSection from './screens/TeamSection';
import FindingsNotesSection from './screens/FindingsNotesSection';
import SignatureSection from './screens/SignatureSection';
import SignatureDialog from '../components/SignatureDialog';
import DraftSaveButton from '../../../components/DraftSaveButton';
import { DRAFT_KEYS } from '../../../utils/draftUtils';

const OperationSheet = () => {
    const { uhid_no } = useParams();
    const navigate = useNavigate();
    
    const {
      formData, setFormData, errors, loading, success,
      signatureDialogOpen, setSignatureDialogOpen, signaturePadRef,
      handleChange, handleSignatureOpen, handleSignatureSave, handleSubmit
    } = useOperationSheet(uhid_no, navigate);

    const handleShare = async () => {
      try {
        const res = await axios.post(`${API_BASE_URL}/generate_for_operationSheet_share_token/${uhid_no}`);
        const link = `${API_BASE_URL}/operationSheet/${uhid_no}?token=${res.data.token}`;
        await navigator.clipboard.writeText(link);
        window.open(`http://wa.me/+916379605575?text=${encodeURIComponent("Filling Operation Sheet: " + link)}`, '_blank');
      } catch (err) {
        console.error(err);
      }
    };

    if (loading && !formData.operation_date) {
      return (
        <Backdrop open={true} sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.drawer + 1 }}>
          <CircularProgress color="inherit" />
        </Backdrop>
      );
    }

    return (
      <ThemeProvider theme={theme}>
        <Container maxWidth="lg" sx={{ my: 4 }}>
          <Paper elevation={4} sx={{ p: 4, borderRadius: 3 }}>
            
            {/* Header */}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <LocalHospitalIcon sx={{ color: '#008080', fontSize: 40 }} />
                    <Typography variant="h4" sx={{ color: '#008080', fontWeight: 'bold' }}>OPERATION SHEET</Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <img src="/logo.png" alt="Logo" style={{ height: 40 }} />
                    <Typography variant="h6" sx={{ color: '#008080', fontWeight: 'bold' }}>OTMS</Typography>
                </Box>
            </Box>

            {/* Actions */}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4, p: 2, bgcolor: '#f0f4f4', borderRadius: 2 }}>
                <Button variant="outlined" onClick={() => navigate(-1)}>Back</Button>
                <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                    <Button variant="contained" onClick={() => navigate('/home')}>Home</Button>
                    <Button variant="contained" color="secondary" onClick={() => navigate(`/video?form=OPERATION SHEET&uhid_no=${uhid_no}`)}>Add Video</Button>
                    <IconButton onClick={handleShare} sx={{ color: '#008080' }}><ShareIcon /></IconButton>
                </Box>
            </Box>

            <Divider sx={{ my: 4 }} />

            {/* Patient Info Display (Optional but helpful) */}
            {formData.patient_name && (
                <Box sx={{ mb: 4, p: 2, border: '1px solid #008080', borderRadius: 2, borderLeft: '6px solid #008080', bgcolor: '#f9ffff' }}>
                    <Typography variant="subtitle1" sx={{ fontWeight: 'bold', color: '#008080' }}>
                        Patient: {formData.patient_name} ({formData.age}y / {formData.sex}) | UHID: {uhid_no}
                    </Typography>
                </Box>
            )}

            <DiagnosisSection formData={formData} handleChange={handleChange} setFormData={setFormData} errors={errors} />
            <Divider sx={{ my: 4 }} />
            
            <ProcedureDetailsSection formData={formData} handleChange={handleChange} setFormData={setFormData} errors={errors} />
            <Divider sx={{ my: 4 }} />
            
            <TeamSection formData={formData} handleChange={handleChange} errors={errors} />
            <Divider sx={{ my: 4 }} />
            
            <FindingsNotesSection formData={formData} handleChange={handleChange} setFormData={setFormData} />
            <Divider sx={{ my: 4 }} />
            
            <SignatureSection formData={formData} handleSignatureOpen={handleSignatureOpen} />

            {/* Footer Actions */}
            <Box sx={{ mt: 6, p: 3, bgcolor: '#f0f4f4', borderRadius: 3, display: 'flex', gap: 2 }}>
                <DraftSaveButton
                    formKey={DRAFT_KEYS.OPERATION_SHEET}
                    uhidNo={uhid_no}
                    formData={formData}
                    onDraftLoaded={(draft) => setFormData(prev => ({ ...prev, ...draft }))}
                    isUpdate={success === "success"}
                    fullWidth
                />
                <Button
                    variant="contained"
                    size="large"
                    fullWidth
                    onClick={handleSubmit}
                    sx={{ bgcolor: '#008080', '&:hover': { bgcolor: '#006666' }, height: 56, fontWeight: 'bold' }}
                >
                    {success === "success" ? 'Update Operation Sheet' : 'Submit Operation Sheet'}
                </Button>
            </Box>

          </Paper>

          <SignatureDialog
            open={signatureDialogOpen}
            onClose={() => setSignatureDialogOpen(false)}
            onClear={() => signaturePadRef.current.clear()}
            onSave={handleSignatureSave}
            signaturePadRef={signaturePadRef}
          />
        </Container>
      </ThemeProvider>
    );
};

export default OperationSheet;
